/**
 * MarkdownPlugin — synchronisation bidirectionnelle entre l'état Lexical
 * et une chaîne Markdown externe.
 *
 *  - Au montage et chaque fois que `value` change depuis l'extérieur,
 *    on convertit le Markdown en arbre Lexical via $convertFromMarkdownString.
 *  - À chaque mutation de l'éditeur, on convertit l'arbre Lexical en
 *    Markdown via $convertToMarkdownString et on remonte la string au parent
 *    (uniquement si elle diffère, pour éviter les boucles inutiles).
 *
 * Les TRANSFORMERS de @lexical/markdown couvrent les éléments classiques
 * (gras, italique, titres, listes, citation, code, lien, séparateur, tableau GFM).
 */

import { useEffect, useRef } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from "@lexical/markdown";

import { TABLE_TRANSFORMER } from "./tableTransformer";

// On ajoute notre transformer GFM tableau aux transformers standards.
// On met TABLE_TRANSFORMER en tête : ElementTransformers sont essayés dans
// l'ordre et celui-ci doit gagner sur d'éventuels matchs plus lâches.
const ALL_TRANSFORMERS = [TABLE_TRANSFORMER, ...TRANSFORMERS];

interface MarkdownPluginProps {
  value: string;
  onChange: (markdown: string) => void;
}

export function MarkdownPlugin({ value, onChange }: MarkdownPluginProps) {
  const [editor] = useLexicalComposerContext();
  // Tient à jour la dernière valeur connue côté Lexical. Sert à filtrer
  // les notifications onChange quand le Markdown généré est identique
  // à la dernière valeur reçue/émise — évite les boucles infinies si le
  // parent renvoie verbatim ce qu'on lui a donné.
  //
  // Initialisé à `null` (et pas à `value`) pour qu'au premier render
  // la garde d'égalité ne court-circuite pas la première conversion :
  // sinon l'éditeur s'afficherait vide quand on lui passe directement
  // une valeur initiale non-vide (cas de l'édition d'un document existant).
  const lastEmittedRef = useRef<string | null>(null);

  // Initialisation + sync externe → interne (Markdown → Lexical)
  useEffect(() => {
    // Si la valeur externe correspond déjà à ce qu'on a émis en dernier,
    // c'est un rebond du parent : on ne touche pas à l'arbre Lexical
    // (sinon on perd le curseur à chaque frappe).
    if (value === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = value;
    editor.update(() => {
      $convertFromMarkdownString(value ?? "", ALL_TRANSFORMERS);
    });
  }, [editor, value]);

  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          const markdown = $convertToMarkdownString(ALL_TRANSFORMERS);
          if (markdown !== lastEmittedRef.current) {
            lastEmittedRef.current = markdown;
            onChange(markdown);
          }
        });
      }}
    />
  );
}
